<template>
	<div class="ci-upload-sales-costs">
		<header class="usc-head">
			<p class="usc-eyebrow">Importaci&oacute;n</p>
			<h2 class="usc-heading">Subir ventas/costos</h2>
			<p class="usc-sub">
				Importa la plantilla estándar de Ventas/Costos Devengados.
			</p>
		</header>
		<!-- Grid de dos columnas: el flujo de subida (body + footer) a la
			izquierda; el contexto de la empresa en el aside de la derecha. -->
		<section class="sci-body">
			<!-- Planilla (.xlsx) -->
			<div class="field-group">
				<div class="field-eyebrow">
					Plantilla de ventas/costos
					<span class="field-eyebrow-note">.xlsx</span>
				</div>
				<p class="field-hint">
					Reemplaza <strong>solo los meses presentes en el archivo</strong>;
					los dem&aacute;s meses no se tocan. Los documentos quedan
					clasificados por las columnas de la plantilla y
					<strong>no se editan en el clasificador</strong> (para corregir,
					corrige el archivo y vuelve a subir el mes). El archivo no se
					guarda: solo se procesa.
				</p>
				<details class="usc-guide">
					<summary class="usc-guide-summary">
						C&oacute;mo preparar el archivo — revisa antes de subir
					</summary>
					<ul class="usc-guide-list">
						<li>
							<strong>Headers (fila 1):</strong> los t&iacute;tulos de las
							columnas de venta y costo deben ser <strong>id&eacute;nticos</strong>
							al nombre de la cuenta en el plan de cuentas del cliente
							(may&uacute;sculas y tildes incluidas); si la cuenta no existe,
							cr&eacute;ala en el plan antes de subir. Las 5 primeras columnas
							conservan los t&iacute;tulos de la plantilla: Fecha, Tipo
							Documento, N&ordm; Documento, Contraparte y Detalle.
						</li>
						<li>
							<strong>Fecha:</strong> la fecha de emisi&oacute;n del documento
							(si se conoce; si no, una fecha del mes al que pertenece la
							operaci&oacute;n). Debe ser una celda de fecha real de Excel, no
							texto — esa fecha define <strong>qu&eacute; mes se reemplaza</strong>
							al importar.
						</li>
						<li>
							<strong>Tipo Documento:</strong> homol&oacute;galo a los tipos de
							documento oficiales de la app. Define el IVA con que se muestra
							el monto en el clasificador: todo se despliega con IVA salvo la
							factura de exportaci&oacute;n (exenta); vac&iacute;o se asume
							boleta. El N&ordm; Documento es informativo y puede quedar
							vac&iacute;o.
							<span class="usc-guide-types">
								Tipos oficiales: Factura afecta, Factura no afecta o exenta,
								Factura de exportaci&oacute;n, Factura de compra, Factura de
								compra electr&oacute;nica, Boleta, Boleta electr&oacute;nica,
								Boleta exenta, Boleta exenta electr&oacute;nica, Boleta de
								Honorarios, Nota de cr&eacute;dito, Nota de d&eacute;bito,
								Nota de cr&eacute;dito de exportaci&oacute;n, Nota de
								d&eacute;bito de exportaci&oacute;n, Gu&iacute;a de despacho,
								Liquidaci&oacute;n factura, Declaraci&oacute;n de Ingreso
								(DIN), Total del mes vale electr&oacute;nico especial y Pago
								electr&oacute;nico.
							</span>
						</li>
						<li>
							<strong>Contraparte:</strong> obligatoria en cada fila
							(m&aacute;ximo 100 caracteres); si no se conoce, escribe
							<strong>N/A</strong>.
						</li>
						<li>
							<strong>Montos:</strong> siempre <strong>netos de IVA</strong>,
							en pesos enteros (sin decimales). La venta nunca va en 0: deja la
							celda vac&iacute;a si esa columna no aplica. Una nota de
							cr&eacute;dito se detecta por el signo: su fila va con los montos
							en negativo (venta y costo).
						</li>
						<li>
							<strong>Una cuenta por fila:</strong> el monto de venta va en
							<strong>una sola columna</strong>; si corresponde a varias
							cuentas, divide en una fila por cuenta.
						</li>
						<li>
							<strong>Costo pareado:</strong> cada venta lleva su costo en la
							columna de costos del mismo orden (1&ordf; de venta &harr;
							1&ordf; de costo, y as&iacute; sucesivamente). Pon
							<strong>0</strong> si esa venta no tiene costo — una columna de
							costos toda en 0 no necesita cuenta en el plan.
						</li>
					</ul>
				</details>
				<UploadFile
					:key="uploadFileKey"
					v-show="!uploading"
					fileName="plantilla de ventas/costos"
					messageFormat="XLSX"
					extensionAccept=".xlsx"
					:isLoading="uploading"
					@uploaded="onUploaded"
					@removed-file="onRemovedFile"
				/>

				<!-- Readout del import async: reemplaza el dropzone mientras corre. -->
				<div v-if="uploading" class="import-readout">
					<div class="import-readout-head">
						<div class="import-readout-text">
							<span class="import-readout-title">{{ readoutTitle }}</span>
							<span v-if="file" class="import-readout-file">{{ file.name }}</span>
						</div>
						<span v-if="displayProgress !== null" class="import-readout-pct">
							{{ displayProgress }}<span class="import-readout-pct-unit">%</span>
						</span>
					</div>
					<div class="track" :class="{ 'is-indeterminate': displayProgress === null }">
						<div
							class="track-fill"
							:style="displayProgress === null ? null : { width: displayProgress + '%' }"
						></div>
					</div>
				</div>
			</div>

			<!-- El archivo reemplaza meses con documentos: pedir confirmación. -->
			<div v-if="pendingConfirmation.length" class="import-confirm">
				<div class="import-confirm-head">
					El archivo reemplaza meses que ya tienen documentos
				</div>
				<p class="import-confirm-hint">
					Al confirmar, cada mes del archivo <strong>reemplaza por completo</strong>
					los documentos de ventas/costos de ese mes. Verifica que el archivo
					traiga el mes entero (un archivo parcial borra lo que no incluye).
				</p>
				<ul class="import-confirm-list">
					<li v-for="item in pendingConfirmation" :key="item.month" class="import-confirm-item">
						<span class="import-confirm-month">{{ item.month }}</span>
						<!-- Docs contra docs (cada fila crea gemelos venta+costo):
							comparar docs con filas parecía perder la mitad de los
							datos. El fallback a filas cubre corridas pre-deploy. -->
						<span class="import-confirm-detail">
							{{ item.existing_docs }} documentos actuales &rarr;
							<template v-if="item.incoming_docs != null">
								{{ item.incoming_docs }} documentos nuevos ({{ item.incoming_rows }} filas)
							</template>
							<template v-else>{{ item.incoming_rows }} filas nuevas</template>
						</span>
					</li>
				</ul>
			</div>

			<!-- Advertencias del archivo (ventas con costo 0, filas duplicadas):
				en estado C gatean la confirmación; tras el éxito quedan visibles
				como registro. -->
			<div v-if="warningsList.length" class="import-warnings">
				<div class="import-warnings-head">
					{{ needsConfirmation ? "Revisa antes de importar" : "Advertencias del archivo" }}
				</div>
				<ul class="import-warnings-list">
					<li v-for="(warning, i) in normalizedWarnings" :key="i" class="import-warnings-item">
						<span class="import-warnings-title">{{ warning.title }}</span>
						<p v-if="warning.detail" class="import-warnings-detail">{{ warning.detail }}</p>
						<p v-if="warning.examples" class="import-warnings-examples">{{ warning.examples }}</p>
					</li>
				</ul>
			</div>

			<!-- Filas que bloquean el import (todo-o-nada): no se subió nada. -->
			<div v-if="invalidRows.length" class="import-errors">
				<div class="import-errors-head">
					No se import&oacute; nada &middot; {{ invalidRows.length }}
					{{ invalidRows.length === 1 ? "fila con problema" : "filas con problemas" }}
				</div>
				<p class="import-errors-hint">
					Corrige estas filas en el Excel y vuelve a subirlo.
				</p>
				<ul class="import-errors-list">
					<li v-for="(err, i) in invalidRows" :key="i" class="import-errors-item">
						<span class="import-errors-row">Fila {{ err.row }}</span>
						<span class="import-errors-reason">{{ err.error }}</span>
					</li>
				</ul>
			</div>

			<!-- Error global del import: queda fijo en la sección (el toast se va). -->
			<div v-if="importError" class="import-errors">
				<div class="import-errors-head">No se import&oacute; el archivo</div>
				<p class="import-errors-hint">{{ importError }}</p>
			</div>
		</section>
		<footer class="sci-foot">
			<b-button
				class="is-primary"
				aria-label="Subir ventas/costos"
				:disabled="!canSubmit"
				:loading="uploading"
				@click="submitFile(needsConfirmation)"
			>
				{{ submitLabel }}
			</b-button>
		</footer>

		<!-- Columna de contexto: qué hay cargado, antes de decidir qué subir. -->
		<aside class="usc-side">
			<!-- El último intento falló: contexto al entrar. Durante el flujo
				activo el error ya se ve en la columna izquierda; acá solo si no. -->
			<div
				v-if="lastRun && lastRun.status === 'E' && !uploading && !importError && !invalidRows.length"
				class="usc-last-error"
			>
				<span class="usc-last-error-title">El &uacute;ltimo intento fall&oacute;</span>
				<p v-if="lastRun.error" class="usc-last-error-text">{{ lastRun.error }}</p>
				<p class="usc-last-error-meta">
					{{ formatDateTime(lastRun.uploaded_at) }}<template v-if="lastRun.created_by"> &middot; {{ lastRun.created_by }}</template>
				</p>
			</div>

			<!-- Última importación exitosa: viene aparte (last_success), así la
				franja no desaparece porque el intento más reciente haya fallado. -->
			<ImportRecordCard
				v-if="lastSuccess"
				title="Última importación"
				:at="lastSuccess.finished_at || ''"
				:by="lastSuccess.created_by || ''"
				:rows="lastSuccessRows"
			/>

			<!-- Último borrado desde el panel (last_deletion): quién sacó qué y
				cuándo. Convive con la ficha de última importación. -->
			<ImportRecordCard
				v-if="lastDeletion"
				title="Última eliminación"
				:at="lastDeletion.finished_at || ''"
				:by="lastDeletion.created_by || ''"
				:rows="lastDeletionRows"
			/>

			<!-- Cobertura mensual: qué meses ya tienen documentos cv (viene del
				status, independiente de la última corrida). La unidad de reemplazo
				es el mes — este grid responde "¿qué está cargado?" antes de subir,
				y en modo seleccionable elige qué meses eliminar. -->
			<div v-if="monthsLoaded.length" class="field-group">
				<div class="field-eyebrow">Meses cargados</div>
				<MonthCoverageGrid
					:monthsLoaded="monthsLoaded"
					selectable
					:selected.sync="selectedMonths"
				/>
				<p class="usc-delete-hint">
					Toca un mes para elegirlo. Sin selecci&oacute;n, se elimina todo
					lo cargado. Los documentos del SII y del mayor no se tocan.
				</p>
				<div class="usc-delete-actions">
					<button
						v-if="selectedMonths.length"
						type="button"
						class="ap-btn ap-btn--ghost"
						:disabled="deleting"
						@click="selectedMonths = []"
					>
						Quitar selecci&oacute;n
					</button>
					<button
						type="button"
						class="ap-btn ap-btn--danger"
						:disabled="!canDelete"
						:aria-label="deleteLabel"
						@click="openDeleteConfirm()"
					>
						{{ deleting ? "Eliminando…" : deleteLabel }}
					</button>
				</div>
			</div>

			<!-- Válvula del bloqueo: qué tipos de documento pueden sumar en las
				cuentas que alimenta el archivo. Sólo aparece si el archivo ya
				alimenta alguna cuenta — sin eso no hay nada que habilitar. -->
			<div v-if="cvUnblockAccounts.length" class="field-group">
				<CvUnblockSection
					:accounts="cvUnblockAccounts"
					:types="cvUnblockTypes"
					:unblockedIds="cvUnblockedIds"
					:saving="savingUnblock"
					@save="saveUnblockConfig"
				/>
			</div>

			<!-- Estado vacío: la empresa no tiene ventas/costos cargados. -->
			<div v-if="lastRun && !hasSideContext" class="usc-empty">
				<span class="usc-empty-title">Sin importaciones todav&iacute;a</span>
				<p class="usc-empty-text">
					Esta empresa no tiene ventas/costos cargados. Sube la plantilla
					para crear los documentos de sus meses.
				</p>
			</div>
		</aside>
	</div>
</template>

<script>
import ImportRecordCard from "@/components/companyInfo/ImportRecordCard";
import { formatMonthLabel } from "@/utils/dateLabels";
import CvUnblockSection from "@/components/companyInfo/CvUnblockSection";
import MonthCoverageGrid from "@/components/companyInfo/MonthCoverageGrid";
import ConfirmRestore from "@/components/modals/ConfirmRestore";
import UploadFile from "@/components/uploadFile/UploadFile";
import {
	deleteSalesCostsDocuments,
	getSalesCostsImportStatus,
	getSalesCostsUnblockConfig,
	saveSalesCostsUnblockConfig,
	uploadSalesCostsImport,
	confirmSalesCostsReplacement,
} from "@/api/carteraAnalista";
import { consoleHandlerError, notificate } from "@/utils/message";
import {
	POLL_MAX_CONSECUTIVE_FAILURES,
	isPollOverdue,
	nextPollDelay,
} from "@/utils/importPolling";
import moment from "moment";

// El import corre async (RQ): el POST responde 202 y el estado se sondea vía
// import_status (status: P/S/E/C) con la misma cadencia que el mayor
// (utils/importPolling: rápido → relajado → lento, sin cortar nunca).

export default {
	name: "CompanyInfoUploadSalesCosts",
	components: {
		CvUnblockSection,
		ImportRecordCard,
		MonthCoverageGrid,
		UploadFile,
	},
	props: {
		idCompany: {
			type: [Number, String],
			required: true,
			default: null,
		},
	},
	data() {
		return {
			file: null,
			uploading: false,
			lastRun: null,
			pollTimer: null,
			// Inicio del sondeo (ms): define la cadencia y el aviso de demora.
			pollStartedAt: null,
			// Errores seguidos al leer el estado; se resetea con cada lectura ok.
			pollFailures: 0,
			pollOverdueNotified: false,
			progress: null,
			// % de la subida del archivo (lo reporta axios mientras el navegador
			// manda los bytes); `progress` es el avance del worker. "uploading"
			// es local: el POST con el archivo todavía no volvió.
			uploadPercent: null,
			uploadPhase: "",
			invalidRows: [],
			// Status 'C': el archivo requiere aprobación previa (reemplazo de
			// meses y/o advertencias) — el botón pasa a confirmar. Si la corrida
			// conservó el archivo en S3 (subida directa, `file_retained`) se
			// confirma por referencia con `pendingRunId`; si no, se re-sube el
			// MISMO archivo con el flag.
			needsConfirmation: false,
			pendingRunId: null,
			pendingFileRetained: false,
			// Diff de meses a reemplazar (parte del estado 'C', puede venir vacío
			// si la confirmación es solo por advertencias).
			pendingConfirmation: [],
			// Advertencias no bloqueantes del archivo (duplicados exactos, etc.).
			warningsList: [],
			// Error global del último import (sin filas): persiste en la sección.
			importError: "",
			// Bumpeando esta key remontamos UploadFile para resetear su chip
			// interno (tras cada intento hay que subir otro archivo).
			uploadFileKey: 0,
			// Meses marcados en el grid para eliminar ('YYYY-MM'); vacío = todo.
			selectedMonths: [],
			deleting: false,
			// Válvula del bloqueo cv: cuentas alimentadas por el archivo, tipos
			// de documento de la empresa y cuáles están habilitados (D6).
			cvUnblockAccounts: [],
			cvUnblockTypes: [],
			cvUnblockedIds: [],
			savingUnblock: false,
		};
	},
	created() {
		this.loadLastRun();
		this.loadUnblockConfig();
	},
	beforeDestroy() {
		this.clearPollTimer();
	},
	computed: {
		canSubmit() {
			return !!this.file && !this.uploading;
		},
		// Lo que muestra la barra: % de subida mientras el archivo viaja, % del
		// worker después; null = indeterminada.
		displayProgress() {
			return this.uploadPhase === "uploading" ? this.uploadPercent : this.progress;
		},
		readoutTitle() {
			return this.uploadPhase === "uploading"
				? "Subiendo el archivo…"
				: "Importando ventas/costos…";
		},
		submitLabel() {
			if (this.uploading) return "Importando…";
			if (!this.needsConfirmation) return "Subir ventas/costos";
			return this.pendingConfirmation.length
				? "Confirmar y reemplazar"
				: "Confirmar e importar";
		},
		// Cobertura mensual para el grid compartido. Se refresca sola: lastRun
		// se pisa con cada respuesta del status (incluida la del fin de un
		// import).
		monthsLoaded() {
			return (this.lastRun && this.lastRun.months_loaded) || [];
		},
		// Última corrida exitosa (viene aparte en el status): la franja no
		// desaparece porque el intento más reciente haya fallado.
		lastSuccess() {
			return (this.lastRun && this.lastRun.last_success) || null;
		},
		// Último borrado desde el panel (viene aparte en el status).
		lastDeletion() {
			return (this.lastRun && this.lastRun.last_deletion) || null;
		},
		// Filas de las fichas de registro (ImportRecordCard filtra vacíos).
		lastSuccessRows() {
			const last = this.lastSuccess || {};
			return [
				{ label: "Documentos", value: last.documents_created },
				{ label: "Filas leídas", value: last.rows_parsed },
				{ label: "Meses", value: this.monthsLabel(last.months), text: true },
			];
		},
		lastDeletionRows() {
			const last = this.lastDeletion || {};
			return [
				{ label: "Documentos eliminados", value: last.documents_deleted },
				{ label: "Meses", value: this.monthsLabel(last.months), text: true },
			];
		},
		// La columna de contexto tiene algo que mostrar (franja de última
		// importación, eliminación o cobertura); si no, va el estado vacío.
		hasSideContext() {
			return (
				!!this.lastSuccess ||
				!!this.lastDeletion ||
				this.monthsLoaded.length > 0
			);
		},
		// Documentos que se eliminarían con la selección actual (o todos).
		documentsToDelete() {
			return this.monthsLoaded
				.filter(
					(item) =>
						!this.selectedMonths.length ||
						this.selectedMonths.includes(item.month)
				)
				.reduce((total, item) => total + (item.documents || 0), 0);
		},
		deleteLabel() {
			const count = this.selectedMonths.length;
			if (!count) return "Eliminar todo lo cargado";
			return count === 1 ? "Eliminar 1 mes" : `Eliminar ${count} meses`;
		},
		// Ni durante un import (el backend respondería 409) ni durante otro
		// borrado.
		canDelete() {
			return !this.uploading && !this.deleting && this.monthsLoaded.length > 0;
		},
		// El backend manda {title, detail, examples}; corridas viejas guardaron
		// strings planos — se normalizan para renderizarlas con el mismo layout.
		normalizedWarnings() {
			return this.warningsList.map((warning) =>
				typeof warning === "string"
					? { title: warning, detail: "", examples: "" }
					: warning
			);
		},
	},
	methods: {
		// "mar 2025, abr 2025" para la fila Meses de las fichas.
		monthsLabel(months) {
			return (months || []).map(formatMonthLabel).join(", ");
		},
		loadLastRun() {
			getSalesCostsImportStatus(this.idCompany)
				.then(({ data }) => {
					this.lastRun = data;
					// Import en curso al entrar (recarga u otra pestaña): retomar
					// el readout y el sondeo — sin esto la sección deja subir de
					// nuevo y el POST muere en el 409 sin explicación.
					if (data.status === "P") {
						this.uploading = true;
						if (typeof data.progress === "number") {
							this.progress = data.progress;
						}
						this.startPolling();
					}
				})
				.catch((error) => {
					consoleHandlerError({
						error,
						customDefaultMessage:
							"No se pudo leer la última importación de ventas/costos",
						empty: true,
					});
				});
		},
		// La configuración de la válvula depende de qué cuentas alimenta el
		// archivo, así que se relee después de cada import y de cada borrado.
		loadUnblockConfig() {
			return getSalesCostsUnblockConfig(this.idCompany)
				.then(({ data }) => this.applyUnblockConfig(data))
				.catch((error) => {
					// Contexto secundario: si no se puede leer, la sección no
					// aparece y listo. Con toast sería ruido en cada visita al
					// tab — y durante un deploy, para todos los analistas a la
					// vez: el frontend sale a S3 antes de que Heroku tenga el
					// endpoint. Queda en la consola.
					consoleHandlerError({
						error,
						prefix: "[cvUnblock]",
						silent: true,
					});
					this.applyUnblockConfig({});
				});
		},
		applyUnblockConfig(data) {
			this.cvUnblockAccounts = data.cv_fed_accounts || [];
			this.cvUnblockTypes = data.types || [];
			this.cvUnblockedIds = data.unblocked_type_ids || [];
		},
		saveUnblockConfig(obligationTypeIds) {
			if (this.savingUnblock) return;
			this.savingUnblock = true;
			return saveSalesCostsUnblockConfig(this.idCompany, obligationTypeIds)
				.then(({ data }) => {
					this.applyUnblockConfig(data);
					notificate(
						"<strong>Tipos habilitados guardados</strong><br>El reporte se recalcula con los cambios.",
						"is-success is-light"
					);
				})
				.catch((error) => {
					consoleHandlerError({
						error,
						customDefaultMessage:
							"No se pudieron guardar los tipos habilitados",
					});
				})
				.finally(() => {
					this.savingUnblock = false;
				});
		},
		formatDateTime(value) {
			return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
		},
		onUploaded(file) {
			this.file = file;
			this.resetConfirmation();
		},
		onRemovedFile() {
			this.file = null;
			this.resetConfirmation();
		},
		resetConfirmation() {
			this.needsConfirmation = false;
			this.pendingConfirmation = [];
			this.pendingRunId = null;
			this.pendingFileRetained = false;
		},
		submitFile(confirmReplacement = false) {
			if (!this.file || this.uploading) return;
			this.uploading = true;
			// 202: el import quedó encolado en RQ; sondeamos el estado.
			this.progress = null;
			this.uploadPercent = null;
			this.invalidRows = [];
			this.warningsList = [];
			this.importError = "";
			// Confirmar una corrida en 'C' que conservó su archivo en S3: por
			// referencia, sin volver a mandar los bytes. En cualquier otro caso
			// (primer intento, o 'C' del multipart) el archivo viaja.
			const byReference =
				confirmReplacement && this.pendingRunId !== null && this.pendingFileRetained;
			let submit;
			if (byReference) {
				submit = confirmSalesCostsReplacement(this.idCompany, this.pendingRunId);
			} else {
				this.uploadPhase = "uploading";
				submit = uploadSalesCostsImport(this.idCompany, this.file, confirmReplacement, {
					onUploadProgress: this.onUploadProgress,
				});
			}
			submit
				.then(() => {
					this.uploadPhase = "";
					this.startPolling({ immediate: true });
				})
				.catch((error) => {
					this.uploading = false;
					this.uploadPhase = "";
					const data = error.response && error.response.data;
					const httpStatus = error.response && error.response.status;
					if (httpStatus === 409) {
						notificate(
							"Ya hay una importación en curso para esta empresa.",
							"is-warning is-light"
						);
					} else if (byReference && httpStatus === 400) {
						// La confirmación por referencia ya no está disponible (el
						// archivo expiró en S3 o se liberó): el archivo sigue en la
						// sección, el próximo clic lo vuelve a subir con el flag.
						this.pendingFileRetained = false;
						notificate(
							"La confirmación ya no está disponible. Vuelve a confirmar para subir el archivo de nuevo.",
							"is-warning is-light"
						);
					} else if (!error.response) {
						// Sin respuesta del servidor: se cortó la conexión o se agotó
						// el tiempo de espera. Con la subida directa la confirmación
						// pudo haber llegado y encolado el import aunque la respuesta
						// se perdiera: se consulta el estado antes de dar por perdido.
						this.resumeIfImportStarted();
					} else {
						consoleHandlerError({
							error,
							customDefaultMessage:
								(data && data.error) || "No se pudo importar el archivo",
							empty: true,
						});
					}
				});
		},
		onUploadProgress(event) {
			// Redondeo entero (regla del repo para porcentajes); sin total conocido
			// la barra sigue indeterminada.
			if (event && event.total) {
				this.uploadPercent = Math.min(100, Math.round((event.loaded / event.total) * 100));
			}
		},
		resumeIfImportStarted() {
			getSalesCostsImportStatus(this.idCompany)
				.then(({ data }) => {
					if (data.status === "P") {
						this.uploading = true;
						notificate(
							"La conexión se cortó, pero la importación quedó en curso.",
							"is-info is-light"
						);
						this.startPolling({ immediate: true });
						return;
					}
					this.reportInterruptedUpload();
				})
				.catch(() => this.reportInterruptedUpload());
		},
		reportInterruptedUpload() {
			// No se encoló nada; el archivo sigue seleccionado para reintentar.
			this.importError =
				"La subida del archivo se interrumpió (conexión o tiempo de espera). " +
				"Vuelve a intentarlo.";
			notificate(
				`<strong>No se pudo subir el archivo</strong><br>${this.importError}`,
				"is-warning is-light"
			);
		},
		// Arranca (o retoma) el sondeo del import en curso. `immediate` lee el
		// estado ya mismo (tras encolar); si no, espera la primera cadencia.
		startPolling({ immediate = false } = {}) {
			this.clearPollTimer();
			this.pollStartedAt = Date.now();
			this.pollFailures = 0;
			this.pollOverdueNotified = false;
			if (immediate) {
				return this.pollImportStatus();
			}
			this.schedulePoll();
			return Promise.resolve();
		},
		pollElapsedMs() {
			return this.pollStartedAt ? Date.now() - this.pollStartedAt : 0;
		},
		schedulePoll() {
			this.clearPollTimer();
			this.pollTimer = setTimeout(this.pollImportStatus, nextPollDelay(this.pollElapsedMs()));
		},
		// Devuelve la promesa de la lectura (los llamadores no la usan; los tests sí).
		pollImportStatus() {
			this.pollTimer = null;
			return getSalesCostsImportStatus(this.idCompany)
				.then(({ data }) => {
					this.pollFailures = 0;
					if (data.status === "S") {
						this.onImportFinished(data, true);
					} else if (data.status === "E") {
						this.onImportFinished(data, false);
					} else if (data.status === "C") {
						// El archivo requiere aprobación previa (reemplazo de meses
						// y/o advertencias como ventas con costo 0): se retiene el
						// archivo en la sección y el botón pasa a confirmar. Si el
						// backend conservó el objeto en S3 la confirmación va por
						// referencia; si no, re-sube el mismo archivo con el flag.
						this.uploading = false;
						this.clearPollTimer();
						this.needsConfirmation = true;
						this.pendingConfirmation = data.replacement_preview || [];
						this.pendingRunId = data.run_id === undefined ? null : data.run_id;
						this.pendingFileRetained = !!data.file_retained;
						this.warningsList = data.warnings || [];
					} else {
						// "P" (o vacío justo tras encolar): actualizar el % (si el
						// worker ya reportó) y seguir sondeando. Pasado el tope de
						// tiempo se avisa una vez, pero el readout sigue vivo y el
						// sondeo lento refresca la sección cuando el worker termine.
						if (typeof data.progress === "number") {
							this.progress = data.progress;
						}
						if (!this.pollOverdueNotified && isPollOverdue(this.pollElapsedMs())) {
							this.pollOverdueNotified = true;
							notificate(
								"La importación está tardando más de lo habitual. " +
									"Seguimos revisando su estado y la sección se actualizará al terminar.",
								"is-warning is-light"
							);
						}
						this.schedulePoll();
					}
				})
				.catch((error) => {
					// Un error al leer el estado no significa que el import haya
					// fallado (el worker sigue): se reintenta y solo tras varios
					// fallos seguidos se avisa, sin apagar el readout.
					this.pollFailures += 1;
					if (this.pollFailures === POLL_MAX_CONSECUTIVE_FAILURES) {
						consoleHandlerError({
							error,
							customDefaultMessage:
								"No se pudo leer el estado de la importación. Seguimos intentando.",
							empty: true,
						});
					}
					this.schedulePoll();
				});
		},
		onImportFinished(data, success) {
			this.progress = success ? 100 : this.progress;
			this.uploading = false;
			this.clearPollTimer();
			// El archivo quedó consumido: para reintentar hay que subir otro.
			this.file = null;
			this.uploadFileKey += 1;
			this.uploadPhase = "";
			this.resetConfirmation();
			// La respuesta final trae la cobertura y las fichas completas; se
			// refresca también en 'E' (el aviso de intento fallido del contexto
			// se oculta mientras el error del flujo activo está en pantalla).
			this.lastRun = data;
			if (success) {
				notificate(
					`<strong>Ventas/costos importados</strong><br>${data.documents_created} documentos creados de ${data.rows_parsed} filas.`,
					"is-success is-light"
				);
				// Advertencias no bloqueantes (duplicados exactos): quedan visibles
				// en la sección aunque el import haya sido exitoso.
				this.warningsList = data.warnings || [];
				// El import pudo alimentar cuentas nuevas: la válvula cambia de
				// alcance con ellas.
				this.loadUnblockConfig();
				this.$emit("imported", data);
			} else if (data.invalid_rows && data.invalid_rows.length) {
				// Todo-o-nada: el worker rechazó filas; se muestran al pie para
				// corregir el archivo y volver a subirlo.
				this.invalidRows = data.invalid_rows;
			} else {
				this.importError = data.error || "";
				notificate(
					`<strong>No se pudo importar el archivo</strong><br>${data.error || ""}`,
					"is-danger is-light"
				);
			}
		},
		openDeleteConfirm() {
			if (!this.canDelete) return;
			const months = this.selectedMonths;
			const docs = this.documentsToDelete;
			const docsText = docs === 1 ? "1 documento" : `${docs} documentos`;
			const scope = months.length
				? `de ${months.length === 1 ? "el mes" : "los meses"} <strong>${months.join(", ")}</strong>`
				: "de <strong>todos los meses cargados</strong>";
			this.$buefy.modal.open({
				parent: this,
				width: "25%",
				hasModalCard: true,
				canCancel: ["escape", "outside"],
				customClass: "center-always-modal",
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: "confirmDelete",
					tittleRestore: months.length
						? "¿Eliminar las ventas/costos de los meses elegidos?"
						: "¿Eliminar todas las ventas/costos cargadas?",
					subTittleRestore:
						`Se eliminarán ${docsText} ${scope}, con su clasificación. ` +
						"Los documentos del SII y del mayor no se tocan. " +
						"Para recuperarlos, vuelve a subir la plantilla de esos meses.",
					msgOkAction: "Eliminar",
				},
				events: {
					confirmDelete: () => this.deleteDocuments(months),
				},
			});
		},
		deleteDocuments(months = []) {
			if (this.deleting) return;
			this.deleting = true;
			return deleteSalesCostsDocuments(this.idCompany, months)
				.then(({ data }) => {
					const docs = data.documents_deleted;
					const monthsCount = (data.months || []).length;
					notificate(
						`<strong>Ventas/costos eliminados</strong><br>${docs} ${docs === 1 ? "documento" : "documentos"} de ${monthsCount} ${monthsCount === 1 ? "mes" : "meses"}.`,
						"is-success is-light"
					);
					this.selectedMonths = [];
					this.$emit("deleted", data);
					// El status trae la cobertura y last_deletion ya actualizados.
					this.loadLastRun();
					// Borrar puede dejar cuentas sin alimentar por el archivo.
					this.loadUnblockConfig();
				})
				.catch((error) => {
					const data = error.response && error.response.data;
					const httpStatus = error.response && error.response.status;
					if (httpStatus === 409) {
						notificate(
							"Hay una importación en curso para esta empresa. Espera a que termine antes de eliminar.",
							"is-warning is-light"
						);
					} else {
						consoleHandlerError({
							error,
							customDefaultMessage:
								(data && data.error) || "No se pudieron eliminar las ventas/costos",
							empty: true,
						});
					}
				})
				.finally(() => {
					this.deleting = false;
				});
		},
		clearPollTimer() {
			if (this.pollTimer) {
				clearTimeout(this.pollTimer);
				this.pollTimer = null;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss) — mismo
// lenguaje visual que CompanyInfoUploadLedger: paper → mist, acento teal de
// estado. Sección de la ficha (renderiza dentro de .ci-panel de CompanyInfo).

// Grid de dos columnas: header a lo ancho; flujo (body fila 2 + footer fila 3)
// a la izquierda, contexto a la derecha. Sin wrappers extra: cada hijo se
// ubica por área. Bajo 900px colapsa a una columna (flujo primero).
.ci-upload-sales-costs {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
	column-gap: 24px;
}

.usc-head {
	grid-column: 1 / -1;
}

.sci-body {
	grid-column: 1;
	grid-row: 2;
}

.sci-foot {
	grid-column: 1;
	grid-row: 3;
}

.usc-side {
	grid-column: 2;
	grid-row: 2 / span 2;
	align-self: start;
	position: sticky;
	top: 24px;
}

@media (max-width: 900px) {
	.ci-upload-sales-costs {
		grid-template-columns: minmax(0, 1fr);
	}

	.sci-body,
	.sci-foot,
	.usc-side {
		grid-column: 1;
		grid-row: auto;
	}

	.usc-side {
		position: static;
		margin-top: 16px;
	}
}

// -- Aviso de último intento fallido (columna de contexto) --------------------
.usc-last-error {
	margin-bottom: 12px;
	padding: 12px 14px;
	background-color: var(--ap-alert-wash);
	border: 1px solid rgba(192, 69, 59, 0.28);
	border-radius: var(--ap-radius-sm);
}

.usc-last-error-title {
	display: block;
	font-size: 12.5px;
	font-weight: 700;
	color: var(--ap-alert);
}

.usc-last-error-text {
	margin: 4px 0 0;
	font-size: 12px;
	line-height: 1.45;
	color: var(--ap-ink-2);
}

.usc-last-error-meta {
	margin: 6px 0 0;
	font-size: 11.5px;
	color: var(--ap-muted);
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
}

// -- Estado vacío de la columna de contexto ----------------------------------
.usc-delete-hint {
	margin: 8px 0 0;
	font-size: 11.5px;
	line-height: 1.45;
	color: var(--ap-muted);
}

.usc-delete-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 10px;
}

// Botones del panel (mismo lenguaje que CompanyInfo_Categories).
.ap-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border: 1px solid transparent;
	border-radius: var(--ap-radius-pill);
	padding: 6px 14px;
	height: auto;
	font-family: var(--ap-font);
	font-size: 12.5px;
	font-weight: 600;
	line-height: 1.2;
	cursor: pointer;
	transition: background-color 0.18s var(--ap-ease),
		color 0.18s var(--ap-ease),
		border-color 0.18s var(--ap-ease),
		transform 0.18s var(--ap-ease);

	&:hover:not([disabled]) {
		transform: translateY(-1px);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}

	&[disabled] {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

.ap-btn--ghost {
	background: var(--ap-paper);
	border-color: var(--ap-line-strong);
	color: var(--ap-ink-2);

	&:hover:not([disabled]) {
		border-color: var(--ap-teal);
		color: var(--ap-teal-deep);
	}
}

.ap-btn--danger {
	background: var(--ap-alert-wash);
	color: var(--ap-alert);

	&:hover:not([disabled]) {
		background: var(--ap-alert);
		color: var(--ap-paper);
	}
}

.usc-empty {
	padding: 14px;
	background-color: var(--ap-mist);
	border: 1px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
}

.usc-empty-title {
	display: block;
	font-size: 12.5px;
	font-weight: 700;
	color: var(--ap-ink);
}

.usc-empty-text {
	margin: 4px 0 0;
	font-size: 12px;
	line-height: 1.45;
	color: var(--ap-slate);
}

.usc-head {
	margin-bottom: 16px;
}

.usc-eyebrow {
	margin: 0 0 6px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

.usc-heading {
	margin: 0;
	font-size: 17px;
	font-weight: 700;
	letter-spacing: -0.015em;
	color: var(--ap-ink);
}

.usc-sub {
	margin: 4px 0 0;
	font-size: 12.5px;
	line-height: 1.4;
	color: var(--ap-slate);
}

.sci-body {
	background-color: var(--ap-paper);
}

.field-group {
	margin-bottom: 12px;
}

.field-group:last-child {
	margin-bottom: 0;
}

.field-eyebrow {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.field-eyebrow::before {
	content: "";
	flex: none;
	width: 2px;
	height: 10px;
	border-radius: 1px;
	background-color: var(--ap-teal);
}

.field-eyebrow-note {
	padding: 1px 8px;
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: none;
	color: var(--ap-slate);
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-pill);
}

.field-hint {
	margin-bottom: 10px;
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--ap-slate);

	strong {
		color: var(--ap-ink-2);
		font-weight: 600;
	}
}

// -- Guía de preparación del archivo (plegable, cerrada por defecto) ---------

.usc-guide {
	margin-bottom: 12px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
}

.usc-guide-summary {
	padding: 8px 12px;
	font-size: 12px;
	font-weight: 600;
	color: var(--ap-ink-2);
	cursor: pointer;
	user-select: none;

	&::marker {
		color: var(--ap-teal);
	}

	&:hover {
		color: var(--ap-ink);
	}
}

.usc-guide-list {
	margin: 0;
	padding: 2px 12px 10px 28px;
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--ap-slate);

	li + li {
		margin-top: 6px;
	}

	strong {
		color: var(--ap-ink-2);
		font-weight: 600;
	}
}

.usc-guide-types {
	display: block;
	margin-top: 3px;
	font-size: 11.5px;
	color: var(--ap-muted);
}

// -- Dropzone (UploadFile compartido) — mismos overrides que UploadLedger ---
.sci-body :deep(.uploadFile),
.sci-body :deep(.upload),
.sci-body :deep(.upload .upload-draggable){
	display: block;
	width: 100%;
	max-width: 100%;
	box-sizing: border-box;
	height: auto;
}

.sci-body :deep(.upload .upload-draggable){
	padding: 0;
	border: 1.5px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	background-color: var(--ap-mist);
	transition: border-color 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease);
}

.sci-body :deep(.upload .upload-draggable:hover),
.sci-body :deep(.upload .upload-draggable.is-hovered){
	border-color: var(--ap-teal);
	background-color: var(--ap-teal-wash);
}

.sci-body :deep(.upload .section){
	width: 100%;
	height: auto;
	padding: 8px 16px;
	box-sizing: border-box;
}

.sci-body :deep(.upload .content),
.sci-body :deep(.upload .content p){
	max-width: 100%;
	white-space: normal;
	overflow-wrap: anywhere;
}

.sci-body :deep(.upload .content p:not(.has-text-grey-light)){
	font-size: 13px;
	font-weight: 500;
	color: var(--ap-ink-2);
}

.sci-body :deep(.upload .content .has-text-grey-light){
	font-size: 12px;
	color: var(--ap-muted) !important;
}

.sci-body :deep(.upload .icon),
.sci-body :deep(.upload .icon.has-text-primary){
	color: var(--ap-teal) !important;
}

.sci-body :deep(.upload .icon.is-large){
	height: 1.6rem;
	width: 1.6rem;
	margin: 0 auto;
}

.sci-body :deep(.upload .icon.is-large .mdi::before){
	font-size: 1.4rem;
}

.sci-body :deep(.upload .content){
	line-height: 1.3;
}

.sci-body :deep(.upload .content p:not(:last-child)){
	margin-bottom: 2px;
}

.sci-body :deep(.uploadFile .tags){
	margin-top: 8px;
	margin-bottom: 0;
}

.sci-body :deep(.uploadFile .tag.is-primary){
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	height: auto;
	margin-bottom: 0;
	padding: 6px 8px 6px 10px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-left: 3px solid var(--ap-teal);
	border-radius: var(--ap-radius-sm);
	color: var(--ap-ink-2);
	font-size: 12.5px;
	font-weight: 500;
	white-space: normal;
	overflow-wrap: anywhere;
}

.sci-body :deep(.uploadFile .tag.is-primary .delete){
	flex: none;
	margin-left: 10px;
	background-color: var(--ap-slate);
}

.sci-body :deep(.uploadFile .tag.is-primary .delete:hover){
	background-color: var(--ap-alert);
}

// -- Readout del import -----------------------------------------------------
.import-readout {
	padding: 16px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
}

.import-readout-head {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
}

.import-readout-text {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.import-readout-title {
	font-size: 13px;
	font-weight: 600;
	color: var(--ap-ink);
}

.import-readout-file {
	font-size: 12px;
	color: var(--ap-slate);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.import-readout-pct {
	flex: none;
	font-size: 18px;
	font-weight: 700;
	color: var(--ap-teal-deep);
	font-variant-numeric: tabular-nums;
	letter-spacing: -0.01em;
}

.import-readout-pct-unit {
	margin-left: 1px;
	font-size: 11px;
	font-weight: 600;
	color: var(--ap-slate);
}

.track {
	position: relative;
	height: 8px;
	border-radius: var(--ap-radius-pill);
	background-color: var(--ap-line);
	overflow: hidden;
}

.track-fill {
	height: 100%;
	border-radius: inherit;
	background: linear-gradient(90deg, var(--ap-teal), var(--ap-teal-deep));
	transition: width 0.4s var(--ap-ease);
}

.track.is-indeterminate .track-fill {
	display: none;
}

.track.is-indeterminate::after {
	content: "";
	position: absolute;
	top: 0;
	bottom: 0;
	width: 38%;
	border-radius: inherit;
	background: linear-gradient(
		90deg,
		transparent,
		var(--ap-teal),
		transparent
	);
	animation: track-slide 1.4s var(--ap-ease) infinite;
}

@keyframes track-slide {
	0% {
		transform: translateX(-120%);
	}
	100% {
		transform: translateX(320%);
	}
}

// -- Confirmación de reemplazo / advertencias ---------------------------------
.import-confirm {
	margin-top: 12px;
	padding: 12px 14px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line-strong);
	border-left: 3px solid var(--ap-teal-deep);
	border-radius: var(--ap-radius-sm);
}

.import-confirm-head {
	font-size: 13px;
	font-weight: 700;
	color: var(--ap-ink);
}

.import-confirm-hint {
	margin-top: 2px;
	margin-bottom: 10px;
	font-size: 12px;
	color: var(--ap-slate);

	strong {
		color: var(--ap-ink-2);
		font-weight: 600;
	}
}

.import-confirm-list {
	margin: 0;
	padding: 0;
	list-style: none;
	border-top: 1px solid var(--ap-line);
}

.import-confirm-item {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 4px 10px;
	padding: 8px 0;
	border-bottom: 1px solid var(--ap-line);
	font-size: 12.5px;
}

.import-confirm-month {
	flex: none;
	font-weight: 700;
	color: var(--ap-ink);
	font-variant-numeric: tabular-nums;
}

.import-confirm-detail {
	flex: 1;
	min-width: 160px;
	color: var(--ap-ink-2);
	font-variant-numeric: tabular-nums;
}

.import-warnings {
	margin-top: 12px;
	padding: 12px 14px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
}

.import-warnings-head {
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: var(--ap-slate);
}

.import-warnings-list {
	margin: 6px 0 0;
	padding: 0;
	list-style: none;
}

.import-warnings-item {
	padding: 8px 0;
	font-size: 12.5px;
	color: var(--ap-ink-2);
}

.import-warnings-item + .import-warnings-item {
	border-top: 1px solid var(--ap-line);
}

.import-warnings-title {
	display: block;
	font-size: 13px;
	font-weight: 700;
	color: var(--ap-ink);
	font-variant-numeric: tabular-nums;
}

.import-warnings-detail {
	margin: 2px 0 0;
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--ap-ink-2);
}

.import-warnings-examples {
	margin: 6px 0 0;
	font-size: 11.5px;
	line-height: 1.5;
	color: var(--ap-muted);
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
}

// -- Filas inválidas / error global -----------------------------------------
.import-errors {
	margin-top: 12px;
	padding: 12px 14px;
	background-color: var(--ap-alert-wash);
	border: 1px solid var(--ap-alert);
	border-radius: var(--ap-radius-sm);
}

.import-errors-head {
	font-size: 13px;
	font-weight: 700;
	color: var(--ap-alert);
}

.import-errors-hint {
	margin-top: 2px;
	margin-bottom: 10px;
	font-size: 12px;
	color: var(--ap-slate);
}

.import-errors-list {
	max-height: 180px;
	overflow-y: auto;
	margin: 0;
	padding: 0;
	list-style: none;
	border-top: 1px solid var(--ap-line);
}

.import-errors-item {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 4px 10px;
	padding: 8px 0;
	border-bottom: 1px solid var(--ap-line);
	font-size: 12.5px;
}

.import-errors-row {
	flex: none;
	font-weight: 700;
	color: var(--ap-ink);
	font-variant-numeric: tabular-nums;
}

.import-errors-reason {
	flex: 1;
	min-width: 160px;
	color: var(--ap-ink-2);
}

// -- Footer + botones ---------------------------------------------------------
.sci-foot {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid var(--ap-line);
}

.ci-upload-sales-costs :deep(.button){
	font-family: var(--ap-font);
	font-weight: 600;
	font-size: 13px;
	height: auto;
	padding: 7px 14px;
	border-radius: var(--ap-radius-sm);
	transition: transform 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease),
		border-color 0.14s var(--ap-ease), color 0.14s var(--ap-ease);

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}

	&[disabled] {
		opacity: 0.55;
		cursor: not-allowed;
	}
}

.sci-foot .button {
	padding: 8px 18px;
}

.ci-upload-sales-costs :deep(.button.is-primary){
	background-color: var(--ap-ink);
	border-color: var(--ap-ink);
	color: var(--ap-paper);

	&:hover:not([disabled]) {
		background-color: var(--ap-teal-deep);
		border-color: var(--ap-teal-deep);
		transform: translateY(-1px);
	}

	&:active:not([disabled]) {
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.track.is-indeterminate::after {
		animation: none;
	}

	.track-fill {
		transition: none;
	}
}
</style>
